import { z } from "zod";
import type { CardStatus } from "./thread.ts";

/**
 * Shared thread-card status contract — the parts both the homepage card (web)
 * and the `thread` artifact card (server) need, so they can't drift. Lives in
 * `types/` because that's the only code path shared web↔api.
 *
 * The api-only derivation (`deriveCardStatus`) and presentation tables
 * (icon/severity) live in `#src/context/cardStatus.ts` — web never needs those.
 */

/** The presentation status a badge actually shows. Superset of `CardStatus`:
 *  splits `waitingForUser` into user-facing "needsAttention" and adds
 *  "archived" (a thread the user has filed away). */
export const derivedThreadStatus = z.enum([
  "inProgress",
  "needsAttention",
  "completed",
  "archived",
  "waiting",
  "paused",
]);
export type DerivedThreadStatus = z.infer<typeof derivedThreadStatus>;

/** `CardStatus` (+ whether the thread is archived) → the badge presentation key.
 *  `isArchived` is a separate UI concern from the status enum (a `completed`
 *  thread the user has archived reads as "Archived", not "Completed"). */
export const cardStatusToDerivedStatus = (
  cardStatus: CardStatus,
  isArchived: boolean,
): DerivedThreadStatus => {
  if (isArchived) return "archived";
  switch (cardStatus) {
    case "waiting":
      return "waiting";
    case "paused":
      return "paused";
    case "waitingForUser":
      return "needsAttention";
    case "inProgress":
      return "inProgress";
    case "completed":
      return "completed";
  }
};

/** User-facing label per status. The homepage badge and the artifact card both
 *  read this — change a label once, both surfaces follow. */
export const THREAD_STATUS_LABEL: Record<DerivedThreadStatus, string> = {
  inProgress: "In progress",
  needsAttention: "Needs attention",
  waiting: "Waiting",
  completed: "Completed",
  archived: "Archived",
  paused: "Paused",
};
